import { Fingering } from "Types";
import IPlayable from "../Playable";
import { ProgressState } from "../Progress";
import { Scene } from "phaser";
import { Config } from "./Types";
import { StaffTransition } from ".";
import { colors } from "Phaser/config";
import ClefMeasure from "./Measure";
import { getColorByCbType } from "Components/Colors";
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from "Utils/Constants";
import Clef from "./Clef";

interface NoteTransitionState {
  stemHeight?: number;
  headScale: number;
  dotScale?: number;
  ledgerLineWidth?: number;
}

export class NoteGraphic
  extends Phaser.GameObjects.Container
  implements IPlayable
{
  isRest = false;
  idx: number;
  resetTimer?: Phaser.Time.TimerEvent;
  isFocused: boolean = false;
  timestamp?: number;
  measureIdx: number;
  timingState?: ProgressState;
  noteHead!: Phaser.GameObjects.Image;
  stem?: Phaser.GameObjects.Rectangle;
  ledgerLines: Phaser.GameObjects.Rectangle[] = [];
  noteLength: number;
  id: string;
  tweenHandles: Phaser.Tweens.Tween[] = [];
  noteDot?: Phaser.GameObjects.Arc;
  pulseObject?: Phaser.GameObjects.Arc;
  pulseTween?: Phaser.Tweens.Tween;
  originalPosition: number;
  fingering?: Fingering;
  fingeringGraphic?: Phaser.GameObjects.Text;
  fingeringGraphicDebug?: Phaser.GameObjects.Rectangle;
  /** Set to true to show debug rectangles for note bounds */
  showFingeringDebug: boolean = false;
  stemDirection: "up" | "down";

  transitionState?: NoteTransitionState;
  midiNote: number;

  line: number;
  unscaledX: number;

  innerContainer?: Phaser.GameObjects.Container;
  debugRectangle: any;

  parentClef: Clef;

  constructor(
    scene: Scene,
    measureIdx: number,
    id: string,
    x: number,
    unscaledX: number,
    line: number,
    length: number,
    { pitchLineGap, pitchLineWidth }: Config,
    timestamp: number,
    idx: number,
    midiNote: number,
    parentClef: Clef,
    fingering?: Fingering,
  ) {
    super(scene);
    this.parentClef = parentClef;
    this.innerContainer = this.scene.add.container(0, 0, []);
    this.add(this.innerContainer);
    this.unscaledX = unscaledX;
    this.setX(x);
    this.line = line;
    this.midiNote = midiNote;
    this.measureIdx = measureIdx;
    this.idx = idx;
    this.fingering = fingering;
    this.timestamp = timestamp;
    this.noteLength = length;
    this.id = id;
    scene.add.existing(this);
    const yPos = (line / 2) * pitchLineGap;
    let dotted = false;
    switch (length) {
      case 0.25:
        this.noteHead = scene.add.image(0, yPos, "quarter_note");
        break;
      case 0.3125:
        this.noteHead = scene.add.image(0, yPos, "quarter_note");
        dotted = true;
        break;
      case 0.5:
        this.noteHead = scene.add.image(0, yPos, "half_note");
        break;
      case 0.75:
        this.noteHead = scene.add.image(0, yPos, "half_note");
        dotted = true;
        break;
      case 1:
        this.noteHead = scene.add.image(0, yPos, "whole_note");
        break;
      case 1.5:
        this.noteHead = scene.add.image(0, yPos, "whole_note");
        dotted = true;
        break;
      default:
        console.error("Only half, quarter and whole notes supported for now!");
    }
    this.noteHead.tintFill = true;
    this.noteHead.tint = 0;
    this.noteHead.setScale(pitchLineGap / this.noteHead.displayHeight);
    this.innerContainer.add(this.noteHead);
    if (dotted) {
      const dotX = this.noteHead.x + this.noteHead.displayWidth * 1.2;
      let yOffset = 0;
      const dotR = this.noteHead.displayWidth / 6;
      if (line % 2 === 0) {
        yOffset = this.noteHead.displayHeight / 3 + dotR / 2;
      }
      const dotY = this.noteHead.y - yOffset;
      this.noteDot = scene.add.circle(dotX, dotY, dotR, 0x0, 1);
      this.innerContainer.add(this.noteDot);
    }
    const ledgerLineWidth = this.noteHead.displayWidth * 1.5;
    // The stem width should be relative to the width of the note head itself,
    // otherwise it doesn't scale properly along with everything else.
    const stemWidth = this.noteHead.displayWidth * 0.1;
    const stemHeight = pitchLineGap * 3.5;
    let stemYOffset = stemHeight / 2;
    let stemXOffset = -this.noteHead.displayWidth / 2 + stemWidth / 2;
    if (line >= 5) {
      this.stemDirection = "up";
      stemYOffset *= -1;
      stemXOffset *= -1;
    } else {
      this.stemDirection = "down";
    }
    this.stem = [0.25, 0.375, 0.5, 0.75].contains(length)
      ? scene.add.rectangle(
          stemXOffset,
          yPos + stemYOffset,
          stemWidth,
          stemHeight,
          0,
          1,
        )
      : undefined;
    this.stem && this.innerContainer.add(this.stem);
    if (line < 0) {
      const numLedgerLines = Math.floor((0 - line) / 2);
      for (let i = 0; i < numLedgerLines; i++) {
        this.ledgerLines.push(
          scene.add.rectangle(
            0,
            (-i - 1) * pitchLineGap,
            ledgerLineWidth,
            pitchLineWidth,
            0x0,
            1,
          ),
        );
      }
    } else {
      const numLedgerLines = Math.floor((line - 8) / 2);
      for (let i = 0; i < numLedgerLines; i++) {
        this.ledgerLines.push(
          scene.add.rectangle(
            0,
            (i + 5) * pitchLineGap,
            ledgerLineWidth,
            pitchLineWidth,
            0x0,
            1,
          ),
        );
      }
    }
    this.ledgerLines.forEach((line) => {
      line.setAlpha(0);
      this.innerContainer?.add(line);
      this.sendToBack(line);
    });
    this.originalPosition = this.x;
    this.noteHead.alpha = 0;
    this.stem?.setAlpha(0);
    this.noteDot?.setAlpha(0);
  }

  setParent(measure: ClefMeasure) {
    measure.add(this);
  }

  resize(pitchLineGap: number, xPosScale: number, fullMeasureWidth: number) {
    this.setX(this.unscaledX * xPosScale);
    this.originalPosition = this.x;
    this.noteHead.setScale(
      (pitchLineGap * window.devicePixelRatio) / this.noteHead.height,
    );
    this.noteHead.setY(
      (this.line / 2) * pitchLineGap * window.devicePixelRatio,
    );
    //this.noteHead.setX(xStartOffset + this.unscaledX * xPosScale);
    const dotX = this.noteHead.x + this.noteHead.displayWidth * 1.2;
    let yOffset = 0;
    const dotR = this.noteHead.displayWidth / 6;
    if (this.line % 2 === 0) {
      yOffset = this.noteHead.displayHeight / 3 + dotR / 2;
    }
    const dotY = this.noteHead.y - yOffset;
    this.noteDot?.setX(dotX);
    this.noteDot?.setY(dotY);
    this.noteDot?.setRadius(dotR);
    const ledgerLineWidth = this.noteHead.displayWidth * 1.5;
    // The stem width should be relative to the width of the note head itself,
    // otherwise it doesn't scale properly along with everything else.
    const stemWidth = this.noteHead.displayWidth * 0.1;
    const stemHeight = pitchLineGap * window.devicePixelRatio * 3.5;
    let stemYOffset = stemHeight / 2;
    let stemXOffset = -this.noteHead.displayWidth / 2 + stemWidth / 2;
    if (this.stemDirection === "up") {
      stemYOffset *= -1;
      stemXOffset *= -1;
    }
    this.stem?.setX(this.noteHead.x + stemXOffset);
    this.stem?.setY(
      (this.line / 2) * pitchLineGap * window.devicePixelRatio + stemYOffset,
    );
    this.stem?.setSize(stemWidth, stemHeight);
    this.ledgerLines.forEach((l, i) => {
      l.setSize(ledgerLineWidth, l.height);
      if (this.line < 0)
        l.setY((-i - 1) * pitchLineGap * window.devicePixelRatio);
      else l.setY((i + 5) * pitchLineGap * window.devicePixelRatio);
    });
  }

  getY() {
    return this.noteHead.y;
  }

  transitionOut() {
    this.addTween({
      targets: [
        ...[this.noteHead],
        ...[this.noteDot],
        ...[this.stem],
        ...[this.fingeringGraphic],
        ...this.ledgerLines,
      ],
      alpha: 0,
      duration: 500,
      ease: "Power2",
    });
  }

  transition(style: StaffTransition) {
    switch (style) {
      case "Draw":
        const transitionState = {
          stemHeight: this.stem?.height,
          headScale: this.noteHead.scale,
          fingeringScale: this.fingeringGraphic?.scale,
          dotScale: this.noteDot?.scale,
          ledgerLineWidth: this.ledgerLines[0]?.width,
          stemY: this.stem?.y,
        };
        this.ledgerLines.forEach((line) => (line.width = 0));
        this.noteHead.setScale(0);
        this.fingeringGraphic?.setScale(0);
        this.noteDot?.setScale(0);
        if (this.stem) {
          this.stem.setY(
            this.stem.y +
              this.stem.displayHeight * (this.stemDirection === "up" ? 1 : 0),
          );
          this.stem.height = 0;
        }
        this.addTween({
          targets: [this.noteHead],
          alpha: 1,
          scale: transitionState.headScale,
          duration: 500,
          ease: "Power2",
          onComplete: () => {
            this.addTween({
              targets: [...this.ledgerLines],
              alpha: 1,
              width: transitionState.ledgerLineWidth,
              duration: 500,
            });
            if (this.stem)
              this.addTween({
                targets: [this.stem],
                alpha: 1,
                y: transitionState.stemY,
                height: transitionState.stemHeight,
                duration: 500,
                ease: "Power2",
              });
            if (this.fingeringGraphic)
              this.addTween({
                targets: [this.fingeringGraphic],
                alpha: 1,
                scale: transitionState.fingeringScale,
                duration: 500,
                ease: "Power2",
              });
            if (this.noteDot)
              this.addTween({
                targets: [this.noteDot],
                scale: transitionState.dotScale,
                alpha: 1,
                duration: 500,
                ease: "Power2",
              });
          },
        });
        break;
      case "None":
        this.ledgerLines.forEach((line) => (line.alpha = 1));
        this.noteHead.setAlpha(1);
        this.noteDot?.setAlpha(1);
        this.fingeringGraphic?.setAlpha(1);
        this.stem?.setAlpha(1);
        break;
      case "Fade":
        this.addTween({
          targets: [
            ...[this.noteHead],
            ...[this.noteDot],
            ...[this.stem],
            ...this.ledgerLines,
            ...[this.fingeringGraphic],
          ],
          alpha: 1,
          duration: 500,
          ease: "Power2",
        });
        break;
    }
  }

  addTween(
    tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig,
  ) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  update(_: number) {}

  destroy() {
    this.tweenHandles.forEach((tween) => tween.remove());
    this.resetTimer?.remove();
    this.isFocused = false;
    this.fingeringGraphic?.destroy();
    this.stem?.destroy();
    this.ledgerLines.forEach((l) => l.destroy());
    this.ledgerLines.clear();
    this.noteHead.destroy();
    this.pulseTween?.stop();
    this.pulseObject?.destroy();
    this.noteDot?.destroy();
    super.destroy();
  }

  setFocus(focus: boolean) {
    this.isFocused = focus;
    this.tweenHandles.forEach((tween) => tween.remove());
  }

  showFingering(
    parent: Phaser.GameObjects.Container,
    max: number,
    min: number,
    maxNote: NoteGraphic,
    minNote: NoteGraphic,
    staffPosition: "top" | "bottom",
    pitchLineGap: number,
  ) {
    const bounds = this.getClefRelativeBounds();
    if (this.fingering) {
      if (!this.fingeringGraphic) {
        const y = staffPosition === "top" ? max : min;
        const x = (bounds.x2 - bounds.x1) / 2;
        if (this.fingering)
          this.fingeringGraphic = this.scene.add.text(
            x,
            y,
            this.fingering.value,
            {
              color: "#000",
              fontSize: pitchLineGap * 1.25 * window.devicePixelRatio,
              fontFamily: "Times New Roman",
            },
          );
      } else {
        const y = staffPosition === "top" ? max : min;
        const x = (bounds.x2 - bounds.x1) / 2;
        this.remove(this.fingeringGraphic);
        this.fingeringGraphic.setFontSize(
          pitchLineGap * 1.25 * window.devicePixelRatio,
        );
        this.fingeringGraphic.setPosition(
          0,
          y /*+
            ((staffPosition === "top" ? -1 : 1) *
              pitchLineGap *
              window.devicePixelRatio) /
              2*/,
        );
        console.debug("bounds", bounds, min, y);
        this.add(this.fingeringGraphic);
        /*if (!this.debugRectangle) {
          this.debugRectangle = this.scene.add.rectangle(
            0,
            0,
            100,
            100,
            0x0000ff,
            0.5,
          );
          this.add(this.debugRectangle);
        }*/
        //this.debugRectangle.setPosition(0, y);
      }
    }
    // Debug rectangles - set showFingeringDebug to true to visualize note bounds
    if (this.showFingeringDebug) {
      if (!this.fingeringGraphicDebug) {
        const x = bounds.x1;
        const y = bounds.y1;
        const width = bounds.x2 - bounds.x1;
        const height = bounds.y2 - bounds.y1;
        if (maxNote == this || minNote == this) {
          this.fingeringGraphicDebug = this.scene.add.rectangle(
            x,
            y,
            width,
            height,
            maxNote == this ? 0xff0000 : 0x0000ff,
            0.5,
          );
          this.add(this.fingeringGraphicDebug!);
        }
      } else {
        const x = bounds.x1;
        const y = bounds.y1;
        const width = bounds.x2 - bounds.x1;
        const height = bounds.y2 - bounds.y1;
        this.fingeringGraphicDebug?.setSize(width, height);
        this.fingeringGraphicDebug.setOrigin(0, 0);
        this.fingeringGraphicDebug.setPosition(x, y);
        this.fingeringGraphicDebug.setFillStyle(
          maxNote == this ? 0xff0000 : 0x0000ff,
          0.5,
        );
      }
    }
    if (staffPosition === "top" && this.fingeringGraphic)
      this.fingeringGraphic.setOrigin(0.5, 1);
    else this.fingeringGraphic?.setOrigin(0.5, 0);
    if (this.fingeringGraphic) this.add(this.fingeringGraphic);
    //parent.add(this.fingeringGraphic);
  }

  // Returns the bounds of the note relative to the highest line in the clef
  getClefRelativeBounds() {
    const noteHeadHeight = this.noteHead.displayHeight;
    const noteHeadWidth = this.noteHead.displayWidth;
    const stemHeight = this.stem
      ? this.stem.displayHeight - noteHeadHeight / 2
      : 0;
    const dotSize = this.noteDot ? this.noteDot.displayWidth : 0;
    const dotXOffset = this.noteDot ? this.noteDot.x - this.noteHead.x : 0;
    //const height = noteHeadHeight + stemHeight;
    const x1 = this.noteHead.x - noteHeadWidth / 2;
    let x2 = this.noteHead.x + noteHeadWidth / 2;
    if (this.noteDot) {
      x2 = this.noteDot.x + this.noteDot.displayWidth / 2;
    }
    const y =
      this.noteHead.y -
      noteHeadHeight / 2 -
      (this.stemDirection === "up" ? stemHeight : 0);
    let y1 = this.noteHead.y - noteHeadHeight / 2;
    let y2 = this.noteHead.y + noteHeadHeight / 2;
    if (this.stem) {
      if (this.stemDirection === "up") {
        y1 -= this.stem.displayHeight - noteHeadHeight / 2;
      } else {
        y2 += this.stem.displayHeight - noteHeadHeight / 2;
      }
    }
    return {
      x1,
      y1,
      x2,
      y2,
    };
  }

  setTintFill(color: number) {
    this.stem && (this.stem.fillColor = color);
    this.noteHead.setTintFill(color);
    this.ledgerLines.forEach((line) => line.setFillStyle(color));
    this.noteDot && (this.noteDot.fillColor = color);
  }

  setTimingState(
    state: ProgressState,
    colorblindType?: COLORBLIND_TYPE | null,
  ) {
    console.debug("[Colorblind] setTimingState", state, colorblindType);
    this.timingState = state;
    switch (state) {
      case ProgressState.LateStart:
      case ProgressState.MistimedRelease:
        // Use colorblind-aware color for semi-error (yellow/orange/grey)
        const semiErrorColor = getColorByCbType(
          ERROR_REC_CLASS.SEMI_ERROR,
          colorblindType ?? null,
        );
        this.setColor(semiErrorColor);
        break;
      case ProgressState.Ended:
        // Use colorblind-aware color for correct (green/blue/white)
        const correctColor = getColorByCbType(
          ERROR_REC_CLASS.CORRECT,
          colorblindType ?? null,
        );
        this.setColor(correctColor);
        break;
      case ProgressState.Missed:
        // Use colorblind-aware color for error (red/brown/black)
        const errorColor = getColorByCbType(
          ERROR_REC_CLASS.ERROR,
          colorblindType ?? null,
        );
        this.setColor(errorColor);
        break;
      default:
    }
  }

  getState() {
    return this.timingState;
  }

  shake() {
    this.tweenHandles.push(
      this.scene.tweens.add({
        targets: { value: 0 },
        value: Math.PI * 2,
        duration: 250,
        repeat: 1,
        onUpdate: (_tween, _target, _key, current) => {
          console.log(current);
          this.setX(this.originalPosition + Math.sin(current) * 10 * 1);
        },
      }),
    );
  }

  reset(force: boolean) {
    this.timingState = undefined;
    const cb = () => {
      this.setTintFill(0);
      this.noteHead.tintFill = true;
      this.pulseObject?.destroy();
      this.pulseObject = undefined;
      this.pulseTween?.remove();
      this.tweenHandles.forEach((tween) => tween.remove());
      this.setX(this.originalPosition);
    };
    /*this.addTween({
      targets: this.noteHead,
      tint: 0,
      duration: force ? 0 : 250,
      delay: force ? 0 : this.idx * 50 + 2000,
    });*/
    /*this.addTween({
      targets: [this.stem, ...this.ledgerLines, this.noteDot],
      fillColor: 0,
      duration: force ? 0 : 250,
      delay: force ? 0 : this.idx * 50 + 2000,
    });*/
    //};
    if (force) {
      this.resetTimer?.remove();
      cb.call(this);
    } else {
      this.resetTimer = this.scene.time.delayedCall(
        2000, //+ this.idx * 50,
        cb,
        [],
        this,
      );
    }
  }

  getPosition() {
    return { x: this.noteHead.x, y: this.noteHead.y };
  }

  getHaloOffset() {
    if (this.noteLength !== 1) {
      return this.height / 3.75;
    } else {
      return 0;
    }
  }

  pulse(colorblindType?: COLORBLIND_TYPE | null) {
    // Get colorblind-aware correct color for pulse
    const correctColorHex = getColorByCbType(
      ERROR_REC_CLASS.CORRECT,
      colorblindType ?? null,
    );
    const correctColor =
      Phaser.Display.Color.ValueToColor(correctColorHex).color;

    if (!this.pulseObject) {
      this.pulseObject = this.scene.add.circle(
        this.noteHead.x,
        this.noteHead.y,
        0,
        correctColor,
        1,
      );
      this.add(this.pulseObject);
    } else {
      // Update color if pulse object already exists
      this.pulseObject.setFillStyle(correctColor);
    }
    this.pulseTween = this.scene.tweens.add({
      targets: this.pulseObject,
      radius: this.noteHead.displayWidth * 2,
      alpha: 0,
      duration: 750,
      onComplete: () => {
        this.pulseObject?.setAlpha(0);
      },
      ease: "Power2",
      callbackScope: this,
    });
  }

  setColor(color: string | number) {
    this.setTintFill(Phaser.Display.Color.ValueToColor(color).color);
  }

  setAlpha(value?: number | undefined): this {
    this.noteDot?.setAlpha(value);
    this.noteHead.setAlpha(value);
    this.ledgerLines.forEach((l) => l.setAlpha(value));
    this.stem?.setAlpha(value);
    this.fingeringGraphic?.setAlpha(value);
    return this;
  }
}

export default NoteGraphic;
